import {useEffect, useMemo} from 'react'
import debounce from 'lodash/debounce'
import {isRefObject} from '../utils/utils'
import useEffectEvent from './use-effect-event'

/**
 * Hook to observe the resize of a target element using ResizeObserver.
 *
 * The hook triggers when the component re-renders after a ref is populated. For refs set via JSX
 * (e.g., `<div ref={myRef}>`), React triggers a re-render during commit, so observation starts
 * automatically. For manual ref updates without re-renders, the hook won't detect changes.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *
 * @param {Element|{current: Element}|null} target - The target element to observe or React ref to one.
 *        Can be null/undefined during initial render; observation starts when element becomes available
 *        and component re-renders.
 * @param {function(ResizeObserverEntry[], ResizeObserver): void} callback - Called when the target element resizes.
 *        Receives an array of ResizeObserverEntry objects containing size information (contentRect,
 *        borderBoxSize, contentBoxSize) and the observer instance.
 * @param {Object} [options] - Optional configuration
 * @param {number} [options.debounceMs] - Debounce delay in milliseconds. Use for performance optimization
 *        when callback triggers expensive operations like layout calculations. When omitted or 0, callback
 *        fires immediately on every resize.
 *
 * @example
 * // Basic usage with ref
 * const divRef = useRef(null)
 * useResizeObserver(divRef, (entries) => {
 *   const {width, height} = entries[0].contentRect
 *   console.log('New size:', width, height)
 * })
 *
 * @example
 * // With debouncing for performance
 * const containerRef = useRef(null)
 * useResizeObserver(containerRef, () => {
 *   // Expensive layout calculation
 *   updateLayout()
 * }, {debounceMs: 100})
 *
 * @example
 * // Observing direct element (less common)
 * const element = document.getElementById('my-element')
 * useResizeObserver(element, (entries) => {
 *   console.log('Element resized:', entries[0].target)
 * })
 */
const useResizeObserver = (target, callback, {debounceMs} = {}) => {
    const onResize = useEffectEvent(callback)

    // Resolve ref outside effect - allows effect to re-run when target.current changes
    // during a re-render (which is the standard React pattern for refs)
    const element = isRefObject(target) ? target.current : target

    // Memoize debounced function to avoid recreation when only element changes
    const debouncedOnResize = useMemo(
        () => (debounceMs > 0 ? debounce(onResize, debounceMs) : null),
        [debounceMs, onResize]
    )

    useEffect(() => {
        if (!element) return

        // Check for ResizeObserver support (defensive, widely supported)
        if (typeof ResizeObserver === 'undefined') {
            return
        }

        const fn = debouncedOnResize || onResize

        const observer = new ResizeObserver(fn)

        observer.observe(element)

        return () => {
            observer.disconnect()
            if (debouncedOnResize) {
                debouncedOnResize.cancel()
            }
        }
    }, [element, debouncedOnResize, onResize])
}

export default useResizeObserver
